/**
 * Tutor filters for the management {@link DataTable}.
 * Each group is a local chip/select block; options follow the active tab.
 */
import * as React from 'react';
import { Minus, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/forms/Button';
import { MultiSelect } from '@/components/forms/MultiSelect';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { SearchInput } from '@/components/forms/SearchInput';
import { DateRangeInput } from '@/components/forms/DateRangeInput';
import { useQuery } from '@tanstack/react-query';
import { useProgramsForMode } from '@/hooks/useProgramsForMode';
import { getLevels } from '@/services/levels.service';
import { useEnterprisesAutocomplete } from '@/hooks/useEnterprisesAutocomplete';
import { useEstablishmentsAutocomplete } from '@/hooks/useEstablishmentsAutocomplete';
import { ManagedByMultiSelect } from '@/components/common/ManagedByMultiSelect';
import {
  type TutorRemoteFilterGroupId as FilterGroupId,
  type TutorRemoteTabView as TabView,
  type TutorRemoteFilters as TutorFilters,
  TUTOR_PRE_REGISTERED_TAB_SITUATION_FILTER_KEYS,
  TUTOR_ACTIVE_TAB_SITUATION_FILTER_KEYS,
  TUTOR_STATUS_FILTER_CHIP_CONFIG,
  type TutorRemoteStatusFilterKey,
  TUTOR_REGISTRATION_STEP_OPTIONS,
  TUTOR_PIPELINE_FILTER_OPTIONS,
  TUTOR_SITUATION_CONFIG,
  GENDER_APPEARANCE,
  GENDER_OPTIONS_WITHOUT_OTHER,
  BOOLEAN_PARTICIPATION_OPTIONS,
  REMOTE_SUBJECT_OPTIONS,
  REMOTE_SUBJECT_CONFIG,
  TUTOR_VALORISATION_FILTER_OPTIONS,
} from '@/components/tutors/tutor-configs';
import { getTutorRemoteStatusFilterKeys } from '@/components/tutors/remote/tutor-table-utils';

export type { TutorFilters, FilterGroupId };
export {
  DEFAULT_TUTOR_REMOTE_FILTERS as DEFAULT_TUTOR_FILTERS,
  ALL_TUTOR_REMOTE_FILTER_GROUP_IDS as ALL_TUTOR_FILTER_GROUP_IDS,
  ALL_TUTOR_MATCHING_FILTER_GROUP_IDS,
  TUTOR_REMOTE_FILTER_LABELS as TUTOR_FILTER_GROUP_LABELS,
} from '@/components/tutors/tutor-configs';

/** Props for {@link TutorFiltersContent}. */
interface Props {
  filters: TutorFilters;
  visibleFilterIds: FilterGroupId[];
  onFilterChange: (patch: Partial<TutorFilters>) => void;
  /** Active tab — drives Situation and Status options. Defaults to `tutors`. */
  activeTab?: TabView;
}

/** Tutor filters for the management {@link DataTable}. */
export const TutorFiltersContent: React.FC<Props> = ({
  filters,
  visibleFilterIds,
  onFilterChange,
  activeTab = 'tutors',
}) => {
  const show = (id: FilterGroupId) => visibleFilterIds.includes(id);

  return (
    <div className="filter-groups-container">
      {show('filter-onboarding-status') && (
        <StatusFilter filters={filters} onFilterChange={onFilterChange} activeTab={activeTab} />
      )}

      {show('filter-folder-situation') && activeTab !== 'archived' && (
        <div className="filter-group">
          <span className="filter-group-label">Situation</span>
          <div className="filter-options">
            {(activeTab === 'pre_registered'
              ? TUTOR_PRE_REGISTERED_TAB_SITUATION_FILTER_KEYS
              : TUTOR_ACTIVE_TAB_SITUATION_FILTER_KEYS
            ).map((key) => {
              const config = TUTOR_SITUATION_CONFIG[key];
              const isActive = filters.tutorSituation === key;
              const Icon = config.icon;
              return (
                <Button
                  key={key}
                  variant="custom"
                  size="sm"
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
                    isActive ? 'filter-badge-active' : 'filter-badge-inactive',
                  )}
                  style={{ '--filter-color': config.badgeColor } as React.CSSProperties}
                  onClick={() => onFilterChange({ tutorSituation: isActive ? undefined : key })}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {config.label}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {show('filter-registration-step') && activeTab === 'pre_registered' && (
        <div className="filter-group">
          <span className="filter-group-label">Étape d'inscription</span>
          <div className="filter-options">
            {TUTOR_REGISTRATION_STEP_OPTIONS.map((opt) => {
              const isActive = (filters.stepKeys ?? []).includes(opt.value);
              const Icon = opt.icon;
              return (
                <Button
                  key={opt.value}
                  variant="custom"
                  size="sm"
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
                    isActive ? 'filter-badge-active' : 'filter-badge-inactive',
                  )}
                  style={{ '--filter-color': opt.color } as React.CSSProperties}
                  onClick={() => {
                    const current = filters.stepKeys ?? [];
                    onFilterChange({
                      stepKeys: isActive
                        ? current.filter((k) => k !== opt.value)
                        : [...current, opt.value],
                    });
                  }}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {opt.label}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {(show('filter-without-information-meeting') ||
        show('filter-without-criminal-record') ||
        show('filter-activatable')) && (
        <div className="filter-group">
          <span className="filter-group-label">Pipeline</span>
          <div className="filter-options">
            {TUTOR_PIPELINE_FILTER_OPTIONS.map((opt) => {
              if (!show(opt.filterId)) return null;
              const isActive = filters[opt.key] === true;
              const Icon = opt.icon;
              return (
                <Button
                  key={opt.filterId}
                  variant="custom"
                  size="sm"
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
                    isActive ? 'filter-badge-active' : 'filter-badge-inactive',
                  )}
                  style={{ '--filter-color': opt.color } as React.CSSProperties}
                  onClick={() => onFilterChange({ [opt.key]: isActive ? undefined : true })}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {opt.label}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {show('filter-search') && (
        <div className="filter-group w-64 min-w-0 shrink-0">
          <span className="filter-group-label">Recherche</span>
          <SearchInput
            className="min-w-0"
            size="sm"
            shortcut={false}
            placeholder="Rechercher un tuteur..."
            value={filters.search ?? ''}
            onChange={(e) => onFilterChange({ search: e.target.value || undefined })}
            onClear={() => onFilterChange({ search: undefined })}
          />
        </div>
      )}

      {show('filter-registration-date') && (
        <div className="filter-group">
          <span className="filter-group-label">Date d'inscription</span>
          <div className="w-auto">
            <DateRangeInput
              layout="horizontal"
              showConnector={true}
              compact={true}
              hidePickerLabels={true}
              showPresets={false}
              startDate={filters.registrationDateFrom}
              endDate={filters.registrationDateTo}
              onStartDateChange={(val) =>
                onFilterChange({ registrationDateFrom: val || undefined })
              }
              onEndDateChange={(val) => onFilterChange({ registrationDateTo: val || undefined })}
            />
          </div>
        </div>
      )}

      {show('filter-program') && (
        <ProgramFilter filters={filters} onFilterChange={onFilterChange} />
      )}

      {show('filter-subject') && (
        <div className="filter-group">
          <span className="filter-group-label">Matière</span>
          <div className="filter-options">
            {REMOTE_SUBJECT_OPTIONS.map((opt) => {
              const isActive = filters.subject === opt.value;
              const config = REMOTE_SUBJECT_CONFIG[opt.value];
              const IconComponent = config?.icon;
              return (
                <Button
                  key={opt.value}
                  variant="custom"
                  size="sm"
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
                    isActive ? 'filter-badge-active' : 'filter-badge-inactive',
                  )}
                  style={{ '--filter-color': config?.badgeColor } as React.CSSProperties}
                  onClick={() => onFilterChange({ subject: isActive ? undefined : opt.value })}
                >
                  {IconComponent && <IconComponent className="w-3.5 h-3.5" />}
                  {opt.label}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {show('filter-gender') && (
        <div className="filter-group">
          <span className="filter-group-label">Genre</span>
          <div className="filter-options">
            {GENDER_OPTIONS_WITHOUT_OTHER.map((opt) => {
              const isActive = filters.gender === opt.value;
              const appearance = GENDER_APPEARANCE[opt.value];
              const IconComponent = appearance.icon;
              return (
                <Button
                  key={opt.value}
                  variant="custom"
                  size="sm"
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
                    isActive ? 'filter-badge-active' : 'filter-badge-inactive',
                  )}
                  style={{ '--filter-color': appearance.color } as React.CSSProperties}
                  onClick={() => onFilterChange({ gender: isActive ? undefined : opt.value })}
                >
                  {IconComponent && <IconComponent className="w-3.5 h-3.5" />}
                  {opt.label}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {show('filter-capacity') && (
        <div className="filter-group">
          <span className="filter-group-label">Capacité</span>
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                'w-7 h-7 p-0 rounded-md flex items-center justify-center transition-all',
                filters.minCapacity === undefined || filters.minCapacity <= 1
                  ? 'opacity-40 cursor-not-allowed'
                  : '',
              )}
              onClick={() => {
                if (filters.minCapacity === undefined || filters.minCapacity <= 1) {
                  onFilterChange({ minCapacity: undefined });
                } else {
                  onFilterChange({ minCapacity: filters.minCapacity - 1 });
                }
              }}
              disabled={filters.minCapacity === undefined || filters.minCapacity <= 1}
            >
              <Minus className="w-3.5 h-3.5" />
            </Button>
            <input
              type="number"
              min={1}
              max={20}
              value={filters.minCapacity ?? ''}
              placeholder=""
              onChange={(e) => {
                const val = e.target.value;
                if (val === '') {
                  onFilterChange({ minCapacity: undefined });
                } else {
                  const n = Number.parseInt(val, 10);
                  if (!Number.isNaN(n) && n >= 1 && n <= 20) onFilterChange({ minCapacity: n });
                }
              }}
              className="w-10 h-7 text-center text-sm font-semibold tabular-nums rounded-md border border-input bg-background focus:outline-none focus:ring-1 focus:ring-ring [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                'w-7 h-7 p-0 rounded-md flex items-center justify-center transition-all',
                (filters.minCapacity ?? 0) >= 20 ? 'opacity-40 cursor-not-allowed' : '',
              )}
              onClick={() =>
                onFilterChange({ minCapacity: Math.min((filters.minCapacity ?? 0) + 1, 20) })
              }
              disabled={(filters.minCapacity ?? 0) >= 20}
            >
              <Plus className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}

      {show('filter-level') && <LevelFilter filters={filters} onFilterChange={onFilterChange} />}

      {show('filter-enterprise') && (
        <EnterpriseFilter filters={filters} onFilterChange={onFilterChange} />
      )}

      {show('filter-establishment') && (
        <EstablishmentFilter filters={filters} onFilterChange={onFilterChange} />
      )}

      {show('filter-criminal-record-validation-date') && (
        <div className="filter-group">
          <span className="filter-group-label">Date de validation du casier</span>
          <div className="w-auto">
            <DateRangeInput
              layout="horizontal"
              showConnector={true}
              compact={true}
              hidePickerLabels={true}
              showPresets={false}
              startDate={filters.cjValidatedFrom}
              endDate={filters.cjValidatedTo}
              onStartDateChange={(val) => onFilterChange({ cjValidatedFrom: val || undefined })}
              onEndDateChange={(val) => onFilterChange({ cjValidatedTo: val || undefined })}
            />
          </div>
        </div>
      )}

      {show('filter-valorisation') && (
        <ValorisationFilter filters={filters} onFilterChange={onFilterChange} />
      )}

      {show('filter-information-meeting-participation') && (
        <div className="filter-group">
          <span className="filter-group-label">Participation à la réunion d'information</span>
          <div className="filter-options">
            {BOOLEAN_PARTICIPATION_OPTIONS.map((opt) => {
              const isActive = filters.participationRI === opt.value;
              const IconComponent = opt.icon;
              return (
                <Button
                  key={String(opt.value)}
                  variant="custom"
                  size="sm"
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
                    isActive ? 'filter-badge-active' : 'filter-badge-inactive',
                  )}
                  style={{ '--filter-color': opt.color } as React.CSSProperties}
                  onClick={() =>
                    onFilterChange({
                      participationRI: isActive ? undefined : opt.value,
                    })
                  }
                >
                  <IconComponent className="w-3.5 h-3.5" />
                  {opt.label}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {show('filter-studies-participation') && (
        <div className="filter-group">
          <span className="filter-group-label">Participation aux études</span>
          <div className="filter-options">
            {BOOLEAN_PARTICIPATION_OPTIONS.map((opt) => {
              const isActive = filters.participationStudies === opt.value;
              const IconComponent = opt.icon;
              return (
                <Button
                  key={String(opt.value)}
                  variant="custom"
                  size="sm"
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
                    isActive ? 'filter-badge-active' : 'filter-badge-inactive',
                  )}
                  style={{ '--filter-color': opt.color } as React.CSSProperties}
                  onClick={() =>
                    onFilterChange({
                      participationStudies: isActive ? undefined : opt.value,
                    })
                  }
                >
                  <IconComponent className="w-3.5 h-3.5" />
                  {opt.label}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {show('filter-managed-by') && (
        <div className="filter-group">
          <span className="filter-group-label">Géré par</span>
          <div className="w-64">
            <ManagedByMultiSelect
              value={filters.managedByIds}
              onChange={(val) => onFilterChange({ managedByIds: val })}
              inputSize="sm"
            />
          </div>
        </div>
      )}
    </div>
  );
};

/** Status chips for the current remote tutor tab. */
const StatusFilter: React.FC<{
  filters: TutorFilters;
  onFilterChange: (patch: Partial<TutorFilters>) => void;
  activeTab: TabView;
}> = ({ filters, onFilterChange, activeTab }) => {
  const statusKeys = getTutorRemoteStatusFilterKeys(activeTab);
  const locked = statusKeys.length <= 1;

  function renderStatusChip(statusKey: TutorRemoteStatusFilterKey): React.ReactNode {
    return (
      <TutorStatusChip
        key={statusKey}
        statusKey={statusKey}
        isSelected={filters.userStatus === statusKey}
        locked={locked}
        onFilterChange={onFilterChange}
      />
    );
  }

  if (statusKeys.length === 0) return null;

  return (
    <div className="filter-group">
      <span className="filter-group-label">Statut</span>
      <div className="filter-options">{statusKeys.map(renderStatusChip)}</div>
    </div>
  );
};

/** One remote tutor status filter chip. */
function TutorStatusChip({
  statusKey,
  isSelected,
  locked,
  onFilterChange,
}: Readonly<{
  statusKey: TutorRemoteStatusFilterKey;
  isSelected: boolean;
  locked: boolean;
  onFilterChange: (patch: Partial<TutorFilters>) => void;
}>): React.ReactNode {
  const config = TUTOR_STATUS_FILTER_CHIP_CONFIG[statusKey];
  const Icon = config.icon;
  const showActive = locked || isSelected;

  function handleClick(): void {
    if (locked) return;
    onFilterChange({ userStatus: isSelected ? undefined : statusKey });
  }

  return (
    <Button
      variant="custom"
      size="sm"
      disabled={locked}
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
        showActive ? 'filter-badge-active' : 'filter-badge-inactive',
        locked && 'opacity-60 cursor-not-allowed',
      )}
      style={{ '--filter-color': config.badgeColor } as React.CSSProperties}
      onClick={handleClick}
    >
      <Icon className="w-3.5 h-3.5" />
      {config.label}
    </Button>
  );
}

/** Program {@link MultiSelect} filter. */
const ProgramFilter: React.FC<{
  filters: TutorFilters;
  onFilterChange: (patch: Partial<TutorFilters>) => void;
}> = ({ filters, onFilterChange }) => {
  const { data: programsData } = useProgramsForMode();

  const options = React.useMemo(() => {
    const seen = new Set<string>();
    return (programsData?.data ?? []).flatMap((p) => {
      const id = p.id?.trim();
      if (!id || seen.has(id)) return [];
      seen.add(id);
      return [{ value: id, label: p.name ?? p.label ?? p.code ?? id }];
    });
  }, [programsData]);

  return (
    <div className="filter-group">
      <span className="filter-group-label">Programme</span>
      <div className="w-64">
        <MultiSelect
          placeholder="Tous les programmes"
          inputSize="sm"
          value={filters.programIds ?? []}
          onChange={(values) =>
            onFilterChange({ programIds: values.length > 0 ? values : undefined })
          }
          options={options}
          maxVisibleTags={2}
        />
      </div>
    </div>
  );
};

/** Company autocomplete filter. */
const EnterpriseFilter: React.FC<{
  filters: TutorFilters;
  onFilterChange: (patch: Partial<TutorFilters>) => void;
}> = ({ filters, onFilterChange }) => {
  const { options, isLoading, onSearch, onLoadMore, hasMore } = useEnterprisesAutocomplete();

  // Internal selected ID — used only to display the selected option in the autocomplete.
  // Filter state keeps the enterprise label in `companyName` (ILIKE backend search).
  const [selectedId, setSelectedId] = React.useState<string>('');

  // Reset internal id when filter is cleared externally
  React.useEffect(() => {
    if (!filters.companyName) setSelectedId('');
  }, [filters.companyName]);

  const handleChange = React.useCallback(
    (id: string) => {
      const opt = options.find((o) => o.value === id);
      setSelectedId(id);
      onFilterChange({ companyName: opt?.label || undefined });
    },
    [options, onFilterChange],
  );

  return (
    <div className="filter-group">
      <span className="filter-group-label">Entreprise</span>
      <div className="w-72">
        <Autocomplete
          placeholder="Rechercher une entreprise..."
          inputSize="sm"
          options={options}
          value={selectedId}
          onChange={handleChange}
          onSearch={onSearch}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          isLoading={isLoading}
          minSearchLength={2}
        />
      </div>
    </div>
  );
};

/** Establishment autocomplete filter. */
const EstablishmentFilter: React.FC<{
  filters: TutorFilters;
  onFilterChange: (patch: Partial<TutorFilters>) => void;
}> = ({ filters, onFilterChange }) => {
  const { options, isLoading, onSearch, onLoadMore, hasMore } = useEstablishmentsAutocomplete();

  const [selectedId, setSelectedId] = React.useState<string>('');

  React.useEffect(() => {
    if (!filters.establishmentName) setSelectedId('');
  }, [filters.establishmentName]);

  const handleChange = React.useCallback(
    (id: string) => {
      const opt = options.find((o) => o.value === id);
      setSelectedId(id);
      onFilterChange({ establishmentName: opt?.label || undefined });
    },
    [options, onFilterChange],
  );

  return (
    <div className="filter-group">
      <span className="filter-group-label">Établissement</span>
      <div className="w-72">
        <Autocomplete
          placeholder="Rechercher un établissement..."
          inputSize="sm"
          options={options}
          value={selectedId}
          onChange={handleChange}
          onSearch={onSearch}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          isLoading={isLoading}
          minSearchLength={2}
        />
      </div>
    </div>
  );
};

/** Level {@link MultiSelect} filter. */
const LevelFilter: React.FC<{
  filters: TutorFilters;
  onFilterChange: (patch: Partial<TutorFilters>) => void;
}> = ({ filters, onFilterChange }) => {
  const { data: levelsData } = useQuery({
    queryKey: ['levels', 'filter'],
    queryFn: () => getLevels({ pageSize: 10, statuses: ['active'] }),
  });

  const options = React.useMemo(
    () => levelsData?.data.map((l) => ({ value: l.id, label: l.label })) ?? [],
    [levelsData],
  );

  return (
    <div className="filter-group">
      <span className="filter-group-label">Niveaux</span>
      <div className="w-64">
        <MultiSelect
          placeholder="Tous les niveaux"
          inputSize="sm"
          value={filters.levelIds ?? []}
          onChange={(values) => onFilterChange({ levelIds: values.length ? values : undefined })}
          options={options}
        />
      </div>
    </div>
  );
};

/** Tutor valorisation filter chips (yes / no / dont-know). */
function ValorisationFilter({
  filters,
  onFilterChange,
}: Readonly<{
  filters: TutorFilters;
  onFilterChange: (patch: Partial<TutorFilters>) => void;
}>): React.ReactNode {
  function renderOption(
    option: (typeof TUTOR_VALORISATION_FILTER_OPTIONS)[number],
  ): React.ReactNode {
    return (
      <ValorisationChip
        key={option.value}
        option={option}
        isSelected={filters.valorisation === option.value}
        onFilterChange={onFilterChange}
      />
    );
  }

  return (
    <div className="filter-group">
      <span className="filter-group-label">Engagement associatif valorisé</span>
      <div className="filter-options">{TUTOR_VALORISATION_FILTER_OPTIONS.map(renderOption)}</div>
    </div>
  );
}

/** One engagement-associatif filter chip. */
function ValorisationChip({
  option,
  isSelected,
  onFilterChange,
}: Readonly<{
  option: (typeof TUTOR_VALORISATION_FILTER_OPTIONS)[number];
  isSelected: boolean;
  onFilterChange: (patch: Partial<TutorFilters>) => void;
}>): React.ReactNode {
  const Icon = option.icon;

  function handleClick(): void {
    onFilterChange({ valorisation: isSelected ? undefined : option.value });
  }

  return (
    <Button
      variant="custom"
      size="sm"
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
        isSelected ? 'filter-badge-active' : 'filter-badge-inactive',
      )}
      style={{ '--filter-color': option.color } as React.CSSProperties}
      onClick={handleClick}
    >
      <Icon className="w-3.5 h-3.5" />
      {option.label}
    </Button>
  );
}
